import React, { createContext, useContext, useState, useEffect } from 'react';
import secureLocalStorage from "react-secure-storage";
import { getImage } from "../../../../network/ApiAxios";

const UserImageContext = createContext();
const IMAGE_CACHE_KEY = "userImageCache"; // Usar uma constante para a chave

export const UserImageProvider = ({ children }) => {
    // Tenta carregar a imagem inicial do localStorage de forma síncrona
    const getInitialImage = () => {
        try {
            const cachedImage = secureLocalStorage.getItem(IMAGE_CACHE_KEY);
            if (cachedImage) {
                const { fileKey, data } = cachedImage;
                const user = JSON.parse(secureLocalStorage.getItem("user"));
                // Valida se a imagem no cache pertence ao usuário atual
                if (user?.image === fileKey) {
                    return { initialContent: data, initialFileKey: fileKey };
                }
            }
        } catch (error) {
            console.error("Failed to load image from localStorage", error);
            secureLocalStorage.removeItem(IMAGE_CACHE_KEY); 

        }
        return { initialContent: null, initialFileKey: null };
    };

    const { initialContent, initialFileKey } = getInitialImage();

    const [imageContent, setImageContent] = useState(initialContent);
    const [currentFileKey, setCurrentFileKey] = useState(initialFileKey);
    const [isLoading, setIsLoading] = useState(false);
    // O imageCache em memória ainda é útil para evitar re-renderizações desnecessárias durante a mesma sessão
    const [imageCache, setImageCache] = useState(initialContent ? { [initialFileKey]: initialContent } : {}); 

    // Carrega a imagem inicial do usuário ao montar o provider
    useEffect(() => {
        const user = JSON.parse(secureLocalStorage.getItem("user"));
        if (user?.image) {
            loadImageWithCache(user.image);
        }
    }, []);

    /**
     * Carrega a imagem com sistema de cache
     * Se a imagem já foi carregada antes, usa o cache
     * Caso contrário, busca do servidor e armazena no cache
     */
    const loadImageWithCache = async (fileKey) => {
        if (!fileKey) {
            setImageContent(null);
            setCurrentFileKey(null);
            secureLocalStorage.removeItem(IMAGE_CACHE_KEY);
            return;
        }

        // Se a fileKey não mudou e já temos a imagem, não faz nada
        if (fileKey === currentFileKey && imageContent) {
            return;
        }

        // Verifica se a imagem está no cache
        if (imageCache[fileKey]) {
            setImageContent(imageCache[fileKey]);
            setCurrentFileKey(fileKey);
            return;
        }

        // Se não está no cache, busca do servidor
        setIsLoading(true);
        try {
            const response = await getImage(fileKey);
            const { data } = response;
            
            if (data) {
                // Armazena no cache em memória
                const newCacheData = { fileKey, data };
                setImageCache(newCacheData);
                
                // Armazena no cache persistente (localStorage)
                secureLocalStorage.setItem(IMAGE_CACHE_KEY, newCacheData); // Salva o objeto no formato esperado

                setImageContent(data);
                setCurrentFileKey(fileKey);
            }
        } catch (error) {
            console.error("Error loading image:", error);
            setImageContent(null);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Atualiza a imagem (usado quando o usuário faz upload de nova foto)
     * Limpa o cache da imagem antiga e carrega a nova
     */
    const refreshImage = async (fileKey) => {
        if (!fileKey) {
            setImageContent(null);
            setCurrentFileKey(null);
            return;
        }

        // Remove a imagem antiga do cache (se existir)
        if(currentFileKey && imageCache[currentFileKey]) {
            const newCache = { ...imageCache };
            delete newCache[currentFileKey];
            setImageCache(newCache);
        }
        secureLocalStorage.removeItem(IMAGE_CACHE_KEY); 

        // Carrega a nova imagem
        await loadImageWithCache(fileKey);
    };

    /**
     * Limpa todo o cache de imagens
     * Útil para logout ou quando necessário liberar memória
     */
    const clearCache = () => {
        setImageCache({});
        setImageContent(null);
        setCurrentFileKey(null);
        secureLocalStorage.removeItem(IMAGE_CACHE_KEY); 

    };

    return (
        <UserImageContext.Provider 
            value={{ 
                imageContent, 
                refreshImage, 
                clearCache,
                isLoading 
            }}
        >
            {children}
        </UserImageContext.Provider>
    );
};

export const useUserImage = () => {
    const context = useContext(UserImageContext);
    if (!context) {
        throw new Error('useUserImage must be used within a UserImageProvider');
    }
    return context;
};